import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  CardActions,
  CardHeader,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Description as FormIcon,
  CalendarToday as CalendarIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Update as UpdateIcon,
} from "@mui/icons-material";
import dataProvider from "../../../components/dataProvider";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";

const PAGE_SIZE = 12;

const ResponseCardSkeleton = () => (
  <Grid item xs={12} sm={6} lg={4}>
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <CardHeader
        avatar={<Skeleton variant="circular" width={40} height={40} />}
        title={<Skeleton variant="text" width="80%" height={28} />}
        subheader={<Skeleton variant="text" width="60%" height={20} />}
      />
      <Divider />
      <CardContent sx={{ flexGrow: 1, pt: 0 }}>
        <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={20} sx={{ mb: 1, borderRadius: 1 }} />
        <Skeleton variant="rectangular" width="80%" height={20} sx={{ borderRadius: 1 }} />
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Box display="flex" gap={1} width="100%">
          <Skeleton variant="rectangular" width="48%" height={36} sx={{ borderRadius: 2 }} />
          <Skeleton variant="rectangular" width="48%" height={36} sx={{ borderRadius: 2 }} />
        </Box>
      </CardActions>
    </Card>
  </Grid>
);

const FormResponseList = ({ customerId }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, response: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuResponse, setMenuResponse] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = PAGE_SIZE;
  const navigate = useNavigate();
  const { id } = useParams(); // id del cliente

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await dataProvider("GET_LIST", "enterprise/form-responses", {
        filter: { customerId },
        pagination: { page, perPage },
        sort: { field: "updatedAt", order: "DESC" }, // Ordenar por fecha de actualización descendente
      });
      setResponses(res.data || []);
      const totalPagesCalc = res.totalPages || Math.ceil((res.totalDocs || 0) / perPage) || 1;
      setTotalPages(totalPagesCalc);
      setLoading(false);
    };
    if (customerId) fetchData();
    // eslint-disable-next-line
  }, [customerId, page]);

  const getFormResponseName = (resp) => resp.name || "Sin nombre";

  const handleDownloadPdf = async (resp) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/enterprise/form-responses/${resp._id || resp.id}/pdf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Error al descargar PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${getFormResponseName(resp).replace(/\s+/g, "_")}_respuesta.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: "Error al descargar PDF", severity: "error" });
    }
  };

  const handleDelete = async (resp) => {
    try {
      await dataProvider("DELETE", "enterprise/form-responses", { id: resp._id || resp.id });
      setSnackbar({ open: true, message: "Respuesta eliminada exitosamente", severity: "success" });
      setResponses(responses.filter((r) => (r._id || r.id) !== (resp._id || resp.id)));
    } catch (err) {
      setSnackbar({ open: true, message: "Error al eliminar respuesta", severity: "error" });
    }
    setDeleteDialog({ open: false, response: null });
  };

  const handleMenuOpen = (event, resp) => {
    setAnchorEl(event.currentTarget);
    setMenuResponse(resp);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuResponse(null);
  };

  const handleEdit = (resp) => {
    navigate(`/enterprise/customers/${id}/forms/editar/${resp._id || resp.id}`);
    handleMenuClose();
  };

  const handleView = (resp) => {
    navigate(`/enterprise/customers/${id}/forms/ver/${resp._id || resp.id}`);
  };

  const handleCreate = () => {
    navigate(`/enterprise/customers/${id}/forms/crear`);
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        sx={{ backgroundColor: "white", p: 2.5, borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
      >
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
            Formularios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Todas las respuestas enviadas por este cliente
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            px: 2.5,
            py: 1.2,
            borderRadius: 2,
            textTransform: "none",
            color: "white",
            fontSize: "0.9rem",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "&:hover": { boxShadow: "0 6px 16px rgba(0,0,0,0.2)" },
          }}
        >
          Crear Formulario
        </Button>
      </Box>
      {loading ? (
        <Grid container spacing={3}>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <ResponseCardSkeleton key={i} />
          ))}
        </Grid>
      ) : responses.length === 0 ? (
        <Card sx={{ textAlign: "center", py: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", borderRadius: 3 }}>
          <CardContent>
            <FormIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom fontWeight="600">
              No hay respuestas registradas
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3} sx={{ maxWidth: 400, mx: "auto" }}>
              Este cliente aún no ha respondido ningún formulario.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: "none", fontSize: "1rem", fontWeight: 600 }}
            >
              Responder formulario
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {responses.map((resp) => (
              <Grid item xs={12} sm={6} lg={4} key={resp._id || resp.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    borderRadius: 3,
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.15)", transform: "translateY(-2px)" },
                  }}
                >
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: "#1976d2" }}>
                        <FormIcon />
                      </Avatar>
                    }
                    action={
                      <Tooltip title="Más opciones">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, resp)}>
                          <MoreIcon />
                        </IconButton>
                      </Tooltip>
                    }
                    title={
                      <Typography variant="h6" component="h2" noWrap fontWeight="600">
                        {getFormResponseName(resp)}
                      </Typography>
                    }
                  />
                  <Divider />
                  <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                    {/* Puedes mostrar un resumen de respuestas aquí si lo deseas */}
                  </CardContent>
                  <Box px={2} pb={3} pt={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Creado: {resp.createdAt ? moment(resp.createdAt).format("DD/MM/YYYY HH:mm") : "-"}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <UpdateIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Actualizado: {resp.updatedAt ? moment(resp.updatedAt).format("DD/MM/YYYY HH:mm") : "-"}
                      </Typography>
                    </Box>
                  </Box>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Box display="flex" gap={1} width="100%">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadPdf(resp)}
                        sx={{ flex: 1, textTransform: "none", borderRadius: 2 }}
                      >
                        Descargar
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleView(resp)}
                        sx={{ flex: 1, textTransform: "none", borderRadius: 2 }}
                      >
                        Ver
                      </Button>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          {/* Controles de paginación */}
          <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
            <Button
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Anterior
            </Button>
            <Typography variant="body2" color="text.secondary">
              Página {page} de {totalPages}
            </Typography>
            <Button
              variant="outlined"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Siguiente
            </Button>
          </Box>
        </>
      )}
      {/* Menú de opciones secundarias */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            handleEdit(menuResponse);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Editar
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setDeleteDialog({ open: true, response: menuResponse });
            handleMenuClose();
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
          <Typography color="error.main">Eliminar</Typography>
        </MenuItem>
      </Menu>
      {/* Diálogo de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, response: null })}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 400 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="600">
            Confirmar eliminación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            ¿Estás seguro de que quieres eliminar esta respuesta? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, response: null })}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleDelete(deleteDialog.response)}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormResponseList;
